import { useState, useEffect } from "react";
import api from "@/services/api";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import {
  Users,
  Phone,
  CheckCircle2,
  FileText,
  Handshake,
  Trophy,
  XCircle,
  Euro,
  AlertCircle,
  GripVertical,
  UserCircle,
  Radar,
  Plus,
  MapPin,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import LeadModal from "@/components/LeadModal";
import DateRangeFilter from "@/components/DateRangeFilter";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const stages = [
  { id: "nuevo", label: "Nuevo", icon: Users, borderColor: "#64748b", headerClass: "kanban-header-nuevo" },
  { id: "contactado", label: "Contactado", icon: Phone, borderColor: "#2563eb", headerClass: "kanban-header-contactado" },
  { id: "calificado", label: "Calificado", icon: CheckCircle2, borderColor: "#0891b2", headerClass: "kanban-header-calificado" },
  { id: "propuesta", label: "Propuesta", icon: FileText, borderColor: "#7c3aed", headerClass: "kanban-header-propuesta" },
  { id: "negociacion", label: "Negociación", icon: Handshake, borderColor: "#d97706", headerClass: "kanban-header-negociacion" },
  { id: "ganado", label: "Ganado", icon: Trophy, borderColor: "#059669", headerClass: "kanban-header-ganado" },
  { id: "perdido", label: "Perdido", icon: XCircle, borderColor: "#dc2626", headerClass: "kanban-header-perdido" },
];

export default function Pipeline({ user }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [regionFilter, setRegionFilter] = useState("todos");
  // v4.4.33 — filtro fecha (creación) cliente
  const [fechaRango, setFechaRango] = useState({ desde: null, hasta: null });
  // v4.4.16: health scores por lead_id (map)
  const [healthMap, setHealthMap] = useState({});

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    try {
      const [leadsResp, healthResp] = await Promise.all([
        api.get('/leads'),
        api.get('/leads-health'),
      ]);
      setLeads(leadsResp.data);
      const map = {};
      for (const item of (healthResp.data?.items || [])) {
        map[item.lead_id] = item;
      }
      setHealthMap(map);
    } catch (error) {
      toast.error("Error al cargar leads");
      console.error("Error fetching leads:", error);
    } finally {
      setLoading(false);
    }
  };

  // Regiones únicas para el dropdown
  const regiones = [...new Set(
    leads
      .map((l) => l.lugar_ejecucion || l.localizacion)
      .filter(Boolean)
  )].sort((a, b) => a.localeCompare(b));

  // v4.4.33 — filtrado combinado: región + rango fecha
  const filteredLeads = leads.filter((l) => {
    if (regionFilter !== "todos" && (l.lugar_ejecucion || l.localizacion || "") !== regionFilter) return false;
    if (fechaRango.desde || fechaRango.hasta) {
      const fecha = String(l.created_at || l.updated_at || "").slice(0, 10);
      if (!fecha) return false;
      if (fechaRango.desde && fecha < fechaRango.desde) return false;
      if (fechaRango.hasta && fecha > fechaRango.hasta) return false;
    }
    return true;
  });

  const getLeadsByStage = (stageId) => {
    return filteredLeads.filter((lead) => lead.etapa === stageId);
  };

  const handleDragEnd = async (result) => {
    const { destination, source, draggableId } = result;

    // No destination or same position
    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;

    const newStage = destination.droppableId;
    const leadId = draggableId;

    // Find the lead being moved
    const movedLead = leads.find((l) => l.lead_id === leadId);
    if (!movedLead) return;

    // Optimistic update
    setLeads((prevLeads) =>
      prevLeads.map((lead) =>
        lead.lead_id === leadId ? { ...lead, etapa: newStage } : lead
      )
    );

    try {
      await api.patch(
        `/leads/${leadId}/stage?etapa=${newStage}`,
        {}
      );
      
      const stageLabel = stages.find(s => s.id === newStage)?.label || newStage;
      toast.success(`${movedLead.empresa} → ${stageLabel}`);
    } catch (error) {
      // Revert on error
      setLeads((prevLeads) =>
        prevLeads.map((lead) =>
          lead.lead_id === leadId ? { ...lead, etapa: source.droppableId } : lead
        )
      );
      toast.error("Error al actualizar etapa");
      console.error("Error updating stage:", error);
    }
  };

  const openModal = (lead) => {
    setSelectedLead(lead);
    setModalOpen(true);
  };

  const closeModal = () => {
    setSelectedLead(null);
    setModalOpen(false);
    fetchLeads();
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-ES", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 0,
    }).format(value);
  };

  const getStageTotal = (stageId) => {
    return getLeadsByStage(stageId).reduce((sum, lead) => sum + (lead.valor_estimado || 0), 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div style={{ color: 'var(--theme-accent)' }} className="animate-pulse">Cargando pipeline...</div>
      </div>
    );
  }

  return (
    <div data-testid="pipeline-page" className="h-full">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => { setSelectedLead(null); setModalOpen(true); }}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-medium transition-colors"
          >
            <Plus className="w-4 h-4" />
            Nuevo Lead
          </button>
          <div>
            <h1 className="text-2xl font-bold font-display theme-text">Pipeline</h1>
            <p className="theme-text-secondary text-sm">Arrastra los leads entre etapas</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Select value={regionFilter} onValueChange={setRegionFilter}>
            <SelectTrigger className="bg-slate-950 border-slate-800 h-9 w-48 text-sm">
              <MapPin className="w-3.5 h-3.5 mr-1 text-slate-500" />
              <SelectValue placeholder="Región" />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-800 max-h-60">
              <SelectItem value="todos">Todas regiones</SelectItem>
              {regiones.map((region) => (
                <SelectItem key={region} value={region}>
                  {region}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {regionFilter !== "todos" && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setRegionFilter("todos")}
              className="h-9 w-9 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-full"
              title="Limpiar filtro"
            >
              <XCircle className="w-4 h-4" />
            </Button>
          )}

          {/* v4.4.33 — Filtro fecha (creación) */}
          <DateRangeFilter
            value={fechaRango}
            onChange={setFechaRango}
            label="Creación"
            compact
          />
        </div>
      </div>

      {/* Kanban Board */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="flex gap-4 overflow-x-auto pb-4" style={{ minHeight: "calc(100vh - 220px)" }}>
          {stages.map((stage) => {
            const stageLeads = getLeadsByStage(stage.id);
            const stageTotal = getStageTotal(stage.id);
            const Icon = stage.icon;

            return (
              <div
                key={stage.id}
                className="flex-shrink-0 w-72 flex flex-col"
                data-testid={`pipeline-column-${stage.id}`}
              >
                {/* Column Header */}
                <div 
                  className={`rounded-t-xl p-4 ${stage.headerClass}`}
                  style={{ borderTop: `4px solid ${stage.borderColor}` }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4 theme-text" />
                      <span className="font-semibold theme-text text-sm">{stage.label}</span>
                    </div>
                    <Badge variant="secondary" className="theme-bg-secondary theme-text" style={{ border: '1px solid var(--theme-border)' }}>
                      {stageLeads.length}
                    </Badge>
                  </div>
                  {stage.id !== "perdido" && (
                    <div className="text-xs" style={{ color: 'var(--theme-currency)' }}>
                      {formatCurrency(stageTotal)}
                    </div>
                  )}
                </div>

                {/* Droppable Area */}
                <Droppable droppableId={stage.id}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={`flex-1 theme-bg-tertiary border-t-0 rounded-b-xl p-2 transition-colors ${
                        snapshot.isDraggingOver ? "ring-2 ring-cyan-500/30" : ""
                      }`}
                      style={{ minHeight: "200px", borderColor: 'var(--theme-border)', borderWidth: '1px', borderStyle: 'solid', borderTopWidth: 0 }}
                    >
                      {stageLeads.length === 0 ? (
                        <div className="text-center py-8 theme-text-muted text-sm">
                          Sin leads
                        </div>
                      ) : (
                        stageLeads.map((lead, index) => (
                          <Draggable
                            key={lead.lead_id}
                            draggableId={lead.lead_id}
                            index={index}
                          >
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                onClick={() => openModal(lead)}
                                className={`mb-2 ${snapshot.isDragging ? "opacity-90" : ""}`}
                                data-testid={`pipeline-card-${lead.lead_id}`}
                              >
                                <Card 
                                  className={`theme-bg-secondary p-3 cursor-pointer hover:shadow-md transition-all group ${
                                    snapshot.isDragging ? "shadow-xl ring-2 ring-cyan-500/30" : ""
                                  }`}
                                  style={{ border: '1px solid var(--theme-border)' }}
                                >
                                  <div className="flex items-start gap-2">
                                    <div
                                      {...provided.dragHandleProps}
                                      className="mt-1 theme-text-muted opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing"
                                    >
                                      <GripVertical className="w-4 h-4" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center justify-between mb-1 gap-2">
                                        <h4 className="font-medium theme-text text-sm truncate">
                                          {lead.empresa}
                                        </h4>
                                        {/* v4.4.16: Badge de salud del lead */}
                                        {healthMap[lead.lead_id] && (
                                          <HealthBadge health={healthMap[lead.lead_id]} />
                                        )}
                                      </div>
                                      <p className="text-xs theme-text-secondary truncate mb-2">
                                        {lead.contacto}
                                      </p>
                                      
                                      {/* Propietario */}
                                      {lead.propietario_nombre && (
                                        <div className="flex items-center gap-1 mb-2">
                                          <UserCircle className="w-3 h-3" style={{ color: 'var(--theme-accent)' }} />
                                          <span className="text-xs" style={{ color: 'var(--theme-accent)' }}>{lead.propietario_nombre}</span>
                                        </div>
                                      )}

                                      {/* Oportunidades count */}
                                      {lead.oportunidades_count > 0 && (
                                        <div className="flex items-center gap-1 mb-2">
                                          <Radar className="w-3 h-3 text-purple-400" />
                                          <span className="text-xs text-purple-400">
                                            {lead.oportunidades_count} oport.
                                          </span>
                                        </div>
                                      )}

                                      <div className="flex items-center justify-between">
                                        <span className="text-xs font-medium" style={{ color: 'var(--theme-currency)' }}>
                                          {formatCurrency(lead.valor_estimado)}
                                        </span>
                                        {lead.dias_sin_actividad > 0 && (
                                          <span className={`text-xs ${
                                            lead.dias_sin_actividad > 7 ? "text-amber-500" : "theme-text-muted"
                                          }`}>
                                            {lead.dias_sin_actividad}d
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </Card>
                              </div>
                            )}
                          </Draggable>
                        ))
                      )}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </div>
            );
          })}
        </div>
      </DragDropContext>

      {/* Lead Modal */}
      <LeadModal
        open={modalOpen}
        onClose={closeModal}
        lead={selectedLead}
      />
    </div>
  );
}


// v4.4.16 — Badge de salud del lead en las cards del Kanban
// v4.4.18 — Tooltip visual shadcn con detalle multi-línea
const NIVEL_EMOJI = { CALIENTE: "🔥", TIBIO: "🟠", FRIO: "🧊", GANADO: "🏆", PERDIDO: "❌" };
const NIVEL_DESCRIPCION = {
  CALIENTE: "Prioridad máxima — trabajar hoy",
  TIBIO: "Normal — mantener momentum",
  FRIO: "Riesgo de perder — reenganchar o descartar",
  GANADO: "Cerrado con éxito",
  PERDIDO: "Cerrado sin éxito",
};

function HealthBadge({ health }) {
  if (!health) return null;
  const { health_score, nivel, senales, dias_sin_actividad, dias_en_etapa } = health;

  const colors = {
    CALIENTE: "bg-emerald-500/20 text-emerald-500 border-emerald-500/40",
    TIBIO: "bg-amber-500/20 text-amber-500 border-amber-500/40",
    FRIO: "bg-red-500/20 text-red-500 border-red-500/40",
    GANADO: "bg-green-500/20 text-green-500 border-green-500/40",
    PERDIDO: "bg-slate-500/20 text-slate-500 border-slate-500/40",
  };
  const cls = colors[nivel] || colors.FRIO;

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={`inline-flex items-center gap-1 px-1.5 py-0 rounded text-[10px] font-bold border shrink-0 cursor-help ${cls}`}
          >
            {health_score}
          </span>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className="max-w-xs p-0 bg-slate-900 border border-slate-700 text-white"
        >
          <div className="p-3 space-y-2">
            <div className="flex items-center gap-2 pb-2 border-b border-slate-700">
              <span className="text-lg">{NIVEL_EMOJI[nivel] || "⚪"}</span>
              <div>
                <div className="font-bold text-sm">
                  {nivel} · {health_score}/100
                </div>
                <div className="text-[11px] text-slate-400">
                  {NIVEL_DESCRIPCION[nivel]}
                </div>
              </div>
            </div>

            <div className="text-[11px] space-y-0.5 text-slate-300">
              {dias_sin_actividad != null && (
                <div>
                  Sin contacto: <span className="font-mono">{dias_sin_actividad}d</span>
                </div>
              )}
              {dias_en_etapa != null && (
                <div>
                  En etapa: <span className="font-mono">{dias_en_etapa}d</span>
                </div>
              )}
            </div>

            {senales && senales.length > 0 && (
              <div className="pt-1 border-t border-slate-700">
                <div className="text-[10px] uppercase text-slate-500 mb-1">Señales</div>
                <div className="flex flex-wrap gap-1">
                  {senales.map((s) => (
                    <span
                      key={s}
                      className="px-1.5 py-0.5 rounded bg-slate-800 text-[10px] text-slate-300"
                    >
                      {s.replace(/_/g, " ")}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
